import { useEffect, useRef, useState } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ReCAPTCHA from "react-google-recaptcha";

// Authentication layout components

// Images
import bgImage from "assets/images/bg-sign-in.jpeg";

// API
import { login } from "api/auth";
import BasicLayout from "authentication/components/BasicLayout";
import { toast } from "react-toastify";

function Basic() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passRecaptcha, setPassRecaptcha] = useState(process.env.REACT_APP_RECAPTCHA_SITE_KEY ? false : true);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const formRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !passRecaptcha) {
      setError("Please complete the reCAPTCHA");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await login(formData);
      if (response.status == 200) {
        toast.success("Login success");
        navigate("/dashboard");
      } else {
        setError(response.detail || "Incorrect username or password");
      }
    } catch (err) {
      if (err?.status === 429) {
        setError("Too many login attempts. Please wait a minute and try again.");
      } else if (err?.status >= 500 || err?.status === undefined) {
        setError("Unable to reach the server. Please try again later.");
      } else {
        setError(err?.data?.message || err?.data?.error || "Incorrect username or password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit if recaptcha passed and username/password filled
  useEffect(() => {
    if (
      passRecaptcha &&
      formData.username &&
      formData.password &&
      !isLoading
    ) {
      handleSubmit();
    }
    // eslint-disable-next-line
  }, [passRecaptcha]);

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox display="flex" justifyContent="center">
              <img src="/efp-logo.png" alt="EFP Logo" style={{ maxWidth: "180px", height: "auto" }} />
            </MDBox>
            <MDTypography variant="h4" textAlign="center" my={3}>Euro Fun Park Admin Panel</MDTypography>
            <MDBox mb={3}>
              <MDInput
                type="text"
                label="Username"
                name="username"
                variant="outlined"
                value={formData.username}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox>
              <MDInput
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </MDBox>
            {error && (
              <MDBox >
                <MDTypography variant="caption" color="error">
                  {error}
                </MDTypography>
              </MDBox>
            )}
            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && <MDBox mt={3}>
              <MDTypography variant="caption">Please verify that you are not a robot</MDTypography>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''}
                onChange={(value) => {
                  setRecaptchaValue(value);
                  setPassRecaptcha(!!value);
                }}
              />
            </MDBox>}
            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="primary"
                fullWidth
                loading={isLoading}
                disabled={!passRecaptcha || !formData.username || !formData.password}
              >
                Login
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
